/**
 * @file QSightChartMetricCard.tsx
 * @description This file contains the QSightMetricCard component, which is used to display a metric card with a title, subtitle, and value.
 * @param, title, subtitle, value, error
 */

import React from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import './QSightChartMetricCard.style.css';
import '../QSightMetricCard.common.css';

export interface QSightChartMetricCardProps {
    metricId?: number;
    title: string;
    subtitle?: string;
    value: string | number | null | undefined;
    graphData?: { label: string; value: number }[];
    error?: string;
    loading?: boolean;
}

const QSightChartMetricCard: React.FC<QSightChartMetricCardProps> = ({
    title,
    subtitle,
    value,
    graphData,
    error,
    loading = false,
}) => {

    const isValueValid = value !== null && value !== undefined && value !== '';
    const isGraphDataValid =
        Array.isArray(graphData) &&
        graphData.length > 0 &&
        graphData.every(
            (item) =>
                item &&
                typeof item.label === 'string' &&
                typeof item.value === 'number'
        );

    const renderHeader = () => (
        <div>
            <div>{title?.trim() || <div className='text-center'>{'No Title Available!'}</div>}</div>
            {subtitle?.trim() && <div className="subtitle bg-info">{subtitle}</div>}
        </div>
    );

    const renderError = () => (
        <div className="text-danger">
            <strong>Error:</strong> {error}
        </div>
    );

    const renderLoading = () => (
        <div className="text-center">
            <Spinner color="primary" />
        </div>
    );

    const renderChart = () => {
        if (!isGraphDataValid) {
            return null;
        }

        return (
            <div className="metric-card-mini-chart">
                <ResponsiveContainer>
                    <AreaChart
                        data={graphData}
                        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    >
                        <XAxis dataKey="label" hide />
                        <YAxis hide />
                        <Area
                            dataKey="value"
                            stroke="#8884d8"
                            fill="grey"
                            style={{ margin: 0 }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderValue = () => {
        if (!isValueValid) {
            return <div className='small'>No data available!</div>;
        }

        return (
            <>
                {renderChart()}
                <div className="text-overlay">{value}</div>
            </>
        );
    };

    return (
        <Card className="qsight-metric-card chart-metric-card">
            <CardHeader className="metric-card-header bg-primary text-white">{renderHeader()}</CardHeader>
            <CardBody className="metric-card-body">
                {loading ? renderLoading() : error ? renderError() : renderValue()}
            </CardBody>
        </Card>
    );
};

export default QSightChartMetricCard;



